import { useId, useState, type FormEvent } from 'react'
import { useImportarPlanilha } from '../../../api/queries/chamados'
import { Botao } from '../../../components/Botao'
import { CampoDeTexto } from '../../../components/campos'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro } from '../../../components/Estados'
import { TEXTOS } from '../../../lib/glossario'
import { ResumoDaCarga } from './ResumoDaCarga'

/**
 * O diálogo que carrega a planilha da fiscalização.
 *
 * É a etapa "b" do termo de recebimento assinado ("Carga da planilha no Sistema de Gestão"), e hoje
 * é a única forma de o sistema conhecer os chamados de um mês — o adaptador de leitura do e-Niterói
 * depende de um token que ainda não existe.
 *
 * O diálogo NÃO se fecha sozinho ao terminar. O resultado da carga é o produto do ato: quantas
 * linhas entraram, quantas foram recusadas e por quê. Fechar em cima do "enviado com sucesso"
 * esconderia justamente a parte que alguém precisa ler — e uma carga que recusa doze linhas em
 * silêncio tira doze chamados do fechamento do mês.
 *
 * Reenviar o mesmo arquivo não é erro e não falha: o servidor reconhece o conteúdo pela impressão
 * digital, não grava nada e devolve o resumo da carga original dizendo isso em uma frase.
 *
 * @param props o que fazer ao fechar
 * @returns o diálogo de carga
 */
export function DialogoDeImportacao({ aoFechar }: { aoFechar: () => void }) {
  const idDoFormulario = useId()
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [competencia, setCompetencia] = useState('')
  const [faltaArquivo, setFaltaArquivo] = useState(false)
  const carga = useImportarPlanilha()

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!arquivo) {
      setFaltaArquivo(true)
      return
    }
    setFaltaArquivo(false)
    carga.mutate({ arquivo, competencia: competencia.trim() || undefined })
  }

  return (
    <Dialogo titulo={TEXTOS.importacao.titulo} descricao={TEXTOS.importacao.descricao} aoFechar={aoFechar}>
      <div className="space-y-5">
        <p className="max-w-prose rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          {TEXTOS.importacao.modelo}
        </p>
        <form id={idDoFormulario} onSubmit={enviar} className="space-y-4">
          {/* Campo de arquivo não é controlado: o navegador não deixa atribuir `value` a um
              `<input type="file">`, por segurança. O estado guarda o `File` escolhido, e o campo
              continua sendo o controle nativo que o teclado e o leitor de tela já sabem operar. */}
          <CampoDeTexto
            rotulo={TEXTOS.importacao.arquivo}
            ajuda={TEXTOS.importacao.ajudaDoArquivo}
            erro={faltaArquivo ? TEXTOS.importacao.faltaArquivo : undefined}
            type="file"
            accept=".xlsx"
            onChange={(evento) => {
              setArquivo(evento.target.files?.[0] ?? null)
              setFaltaArquivo(false)
            }}
          />
          <CampoDeTexto
            rotulo="Mês de referência"
            ajuda={TEXTOS.importacao.ajudaDaCompetencia}
            placeholder="2026-05"
            inputMode="numeric"
            value={competencia}
            onChange={(evento) => setCompetencia(evento.target.value)}
          />
        </form>

        {carga.isError && <AvisoDeErro erro={carga.error} titulo="A planilha não foi carregada" />}
        {carga.data && <ResumoDaCarga resposta={carga.data} />}
      </div>

      <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Botao onClick={aoFechar}>{carga.data ? 'Fechar' : 'Cancelar'}</Botao>
        {/* `form` aponta para o formulário acima: o botão vive no rodapé, fora dele, e é assim que
            o HTML liga os dois sem embrulhar o diálogo inteiro num `<form>`. */}
        <Botao type="submit" form={idDoFormulario} aparencia="primario" disabled={carga.isPending}>
          {carga.isPending ? TEXTOS.importacao.enviando : TEXTOS.importacao.enviar}
        </Botao>
      </div>
    </Dialogo>
  )
}
